'use client'

import React, { memo } from 'react'
import { Button } from '../ui/button'
import ToggleSwitch from '../ui/toggle-switch'
import { SettingsState } from '@/types/settings'
import { cn } from '@/lib/utils'

interface InterfaceSettingsPanelProps {
  settings: SettingsState
  onSettingChange: (key: keyof SettingsState, value: any) => void
  onResetSection: (section: string) => void
}

const InterfaceSettingsPanel = memo<InterfaceSettingsPanelProps>(({
  settings,
  onSettingChange,
  onResetSection
}) => {
  return (
    <div className="space-y-6">
      {/* STATUS & RESET BAR */}
      <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <div className="flex items-center space-x-4 text-xs">
          <span className="text-slate-400">Language:</span>
          <span className="text-blue-400 font-mono uppercase">{settings.language}</span>
          <span className="text-slate-400">Layout:</span>
          <span className="text-green-400 font-mono">{settings.layout}</span>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => onResetSection('interface')}
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
          >
            Reset
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Language - GLOBALIZATION POWER */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="language">
            Language <span className="text-blue-400 uppercase">({settings.language})</span>
          </label>
          <select
            id="language"
            value={settings.language}
            onChange={(e) => onSettingChange('language', e.target.value)}
            className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-slate-200 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
            aria-label="Select interface language"
          >
            <option value="en">🇺🇸 English (Professional)</option>
            <option value="es">🇪🇸 Español (Latino)</option>
            <option value="fr">🇫🇷 Français (European)</option>
            <option value="de">🇩🇪 Deutsch (Technical)</option>
            <option value="zh">🇨🇳 中文 (Global)</option>
            <option value="ja">🇯🇵 日本語 (Precision)</option>
          </select>
        </div>

        {/* Layout - WORKSPACE POWER */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="layout">
            Layout <span className="text-green-400 capitalize">({settings.layout})</span>
          </label>
          <select
            id="layout"
            value={settings.layout}
            onChange={(e) => onSettingChange('layout', e.target.value)}
            className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-slate-200 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
            aria-label="Select interface layout"
          >
            <option value="default">🎯 Default (Balanced)</option>
            <option value="compact">📦 Compact (Space Efficient)</option>
            <option value="expanded">🌌 Expanded (Maximum Detail)</option>
            <option value="minimal">⚡ Minimal (Raw Performance)</option>
            <option value="studio">🎵 Studio (Professional)</option>
          </select>
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200">
          Keyboard Shortcuts <span className="text-purple-400">{settings.keyboardShortcuts ? 'ENABLED' : 'DISABLED'}</span>
        </label>
        <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
            <div><kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-300">Ctrl+S</kbd> Export</div>
            <div><kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-300">Ctrl+R</kbd> Reset</div>
            <div><kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-300">Esc</kbd> Close</div>
            <div><kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-300">Tab</kbd> Navigate</div>
          </div>
        </div>
      </div>

      {/* POWER TOGGLES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Animations Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Animations</h4>
            <p className="text-xs text-slate-400">Smooth transitions</p>
          </div>
          <ToggleSwitch
            value={settings.enableAnimations}
            onChange={(value) => onSettingChange('enableAnimations', value)}
            activeColor="cyan"
            title="Toggle animations"
            ariaLabel={(value) => value ? "Disable animations" : "Enable animations"}
          />
        </div>

        {/* High Contrast Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">High Contrast</h4>
            <p className="text-xs text-slate-400">Accessibility</p>
          </div>
          <ToggleSwitch
            value={settings.highContrast}
            onChange={(value) => onSettingChange('highContrast', value)}
            activeColor="orange"
            title="Toggle high contrast"
            ariaLabel={(value) => value ? "Disable high contrast" : "Enable high contrast"}
          />
        </div>

        {/* Keyboard Shortcuts Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Shortcuts</h4>
            <p className="text-xs text-slate-400">Hotkeys</p>
          </div>
          <ToggleSwitch
            value={settings.keyboardShortcuts}
            onChange={(value) => onSettingChange('keyboardShortcuts', value)}
            activeColor="purple"
            title="Toggle keyboard shortcuts"
            ariaLabel={(value) => value ? "Disable keyboard shortcuts" : "Enable keyboard shortcuts"}
          />
        </div>
      </div>

      {/* Auto-Save Toggle */}
      <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <div>
          <h4 className="text-sm font-medium text-slate-200">Auto-Save</h4>
          <p className="text-xs text-slate-400">Automatically save settings every 1 second</p>
        </div>
        <ToggleSwitch
          value={settings.autoSave}
          onChange={(value) => onSettingChange('autoSave', value)}
          activeColor="green"
          title="Toggle auto-save"
          ariaLabel={(value) => value ? "Disable auto-save" : "Enable auto-save"}
        />
      </div>
    </div>
  )
})

InterfaceSettingsPanel.displayName = 'InterfaceSettingsPanel'

export default InterfaceSettingsPanel